import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMemberAuth } from "@/lib/memberAuth";
import { toast } from "sonner";
import { Key, ShieldCheck } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function MemberSetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setPasswordWithToken } = useMemberAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This link is missing a token. Please use the link exactly as it was sent.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await setPasswordWithToken(token, password);
      toast.success("Password set. Welcome in.");
      navigate("/portal");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not set password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6 relative overflow-hidden" data-testid="member-set-password-page">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="relative w-full max-w-md bg-white p-10 border border-black/5 shadow-2xl">
        <div className="mb-8">
          <Logo size="md" asLink={false} showTagline={false} testId="set-pw-logo" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-navy/50">Set your password</div>
        </div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
          <ShieldCheck size={26} weight="duotone" className="text-ember" /> Set password
        </h1>
        <p className="text-sm text-navy/60 mt-2">
          One-time link. Choose a strong password (10+ characters). This link expires shortly.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="overline mb-2 block">New password</label>
            <input
              data-testid="set-pw-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              required
              minLength={10}
            />
          </div>
          <div>
            <label className="overline mb-2 block">Confirm password</label>
            <input
              data-testid="set-pw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              required
              minLength={10}
            />
          </div>
          {error && (
            <div className="text-xs text-red-700 border border-red-300 bg-red-50 px-3 py-2" data-testid="set-pw-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading || !token} data-testid="set-pw-submit" className="btn-primary w-full justify-center">
            <Key size={16} /> {loading ? "Setting…" : "Set password & sign in"}
          </button>
        </form>
        <div className="mt-6 text-[11px] text-navy/50 leading-relaxed">
          Link expired or lost?{" "}
          <Link to="/portal/forgot-password" className="text-navy underline hover:text-ember" data-testid="set-pw-forgot-link">
            Request a new one
          </Link>
          .
        </div>
      </div>
    </div>
  );
}
